import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import {
  ingresosGastosPorMes,
  resumenAhorro,
} from '#components/mobile/informes/calculos';
import {
  formatPorcentaje,
  TarjetaInforme,
} from '#components/mobile/informes/comunes';
import {
  COLOR_GASTOS,
  COLOR_INGRESOS,
  fraseAhorro,
} from '#components/mobile/informes/IngresosGastosPage';
import { useCategoriasExcluidas } from '#components/mobile/informes/useCategoriasExcluidas';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

import { BarrasPares } from './graficas';

/**
 * «Ahorro»: lo que te quedó el último mes completo frente a tu media. Ancha =
 * + entró/salió de los últimos meses cerrados con datos.
 */
export function TarjetaAhorro({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, meses, isLoading } = useTotalesMensuales({
    meses: 13,
    categoriasExcluidas: excluidas,
  });
  const porMes = ingresosGastosPorMes(
    movimientos,
    meses.slice(-12),
    categorias,
  );
  const { cerrado, habitual } = resumenAhorro(porMes);
  const base = cerrado
    ? fraseAhorro(
        t,
        cerrado.tasa,
        cerrado.ahorro,
        fmt,
        monthUtils.format(cerrado.mes, 'MMMM', locale),
      )
    : { frase: '', tono: 'neutro' as const };
  const comparacion =
    cerrado?.tasa != null && habitual != null
      ? cerrado.tasa >= habitual
        ? t('More than your average ({{pct}}).', {
            pct: formatPorcentaje(habitual, false),
          })
        : t('Less than your average ({{pct}}).', {
            pct: formatPorcentaje(habitual, false),
          })
      : '';
  // Solo meses cerrados con datos: los huecos del principio no dicen nada.
  const cerrados = porMes
    .slice(0, -1)
    .filter(m => m.ingresos > 0 || m.gastos > 0)
    .slice(-6);

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-ahorro"
      titulo={t('Savings')}
      valor={
        isLoading
          ? '…'
          : cerrado?.tasa == null
            ? '—'
            : formatPorcentaje(cerrado.tasa, false)
      }
      frase={
        isLoading || tamano === 'compacto'
          ? ''
          : [base.frase, comparacion].filter(Boolean).join(' ')
      }
      tono={isLoading ? 'neutro' : base.tono}
      onPress={() => void navigate('/reports/ingresos-gastos')}
    >
      {!isLoading && tamano !== 'compacto' && cerrados.length > 1 && (
        <View>
          <BarrasPares
            meses={cerrados.map(m => ({
              etiqueta: monthUtils.format(m.mes, 'MMM', locale),
              a: m.ingresos,
              b: m.gastos,
            }))}
            colorA={COLOR_INGRESOS}
            colorB={COLOR_GASTOS}
          />
        </View>
      )}
    </TarjetaInforme>
  );
}
